'use client';

import React from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '../../primitives/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../primitives/tooltip';
import { cn } from '../../utils/cn';

import type { ComponentPropsWithoutRef, ReactNode } from 'react';

export interface UzPopoverProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger: ReactNode;
  children: ReactNode;
  side?: ComponentPropsWithoutRef<typeof PopoverContent>['side'];
  align?: ComponentPropsWithoutRef<typeof PopoverContent>['align'];
  className?: string;
  /**
   * Optional hover/focus tooltip on the trigger (e.g. a collapsed-rail icon
   * whose meaning isn't obvious until opened). Composes the Radix Tooltip and
   * Popover triggers on the same element - both forward via `asChild`, so the
   * click-to-open behaviour is unaffected.
   */
  tooltip?: ReactNode;
  tooltipSide?: ComponentPropsWithoutRef<typeof TooltipContent>['side'];
  tooltipDelay?: number;
}

/**
 * Simple controlled popover wrapper. Use UzDropdownMenu instead when items
 * are clickable menu options.
 */
export const UzPopover = ({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  children,
  side = 'bottom',
  align = 'start',
  className,
  tooltip,
  tooltipSide = 'right',
  tooltipDelay = 200,
}: UzPopoverProps): React.JSX.Element => (
  <Popover open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
    {tooltip ? (
      <TooltipProvider delayDuration={tooltipDelay}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide}>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
    )}
    <PopoverContent side={side} align={align} className={cn(className)}>
      {children}
    </PopoverContent>
  </Popover>
);
